import { useId, useState, type FormEvent } from 'react';
import { apiErrorText, type CeoBankAccount, useCreateCeoBankPaymentMutation } from '@shared/api';
import { todayIso } from '@shared/lib/localDate';
import { parseMoney } from '@shared/lib/money';
import { Button } from '@shared/ui/Button';
import { formatMoney, PAYMENT_CATEGORY_LABELS } from '../ceoFormat';
import { WrapButton } from '../ui';
import { accountTail } from './paymentView';
import {
  ErrorNotice,
  FieldBox,
  FieldError,
  FieldLabel,
  FormActions,
  FormBox,
  FormGrid,
  FormTitle,
  Input,
  ManualNotice,
  Select,
} from './styles';

interface ManualPaymentFormProps {
  accounts: CeoBankAccount[];
  onClose: () => void;
}

/** Статьи, которые принимает операция банка (`ceo_bank_payments.category`): «Логистики» там
 *  нет — сервер такую запись отклонил бы. */
const CATEGORY_OPTIONS = Object.entries(PAYMENT_CATEGORY_LABELS).filter(
  ([code]) => code !== 'logistics',
);

/**
 * Операция вручную — по счёту без подключения к банку: запись меняет учётный остаток счёта
 * в ОСНОВЕ, в банк ничего не уходит. По счёту с банком операции приходят из его выписок, и
 * ручная запись задвоила бы банковскую: такой счёт не выбрать, сервер тоже откажет.
 */
export function ManualPaymentForm({ accounts, onClose }: ManualPaymentFormProps) {
  const [createPayment, { isLoading }] = useCreateCeoBankPaymentMutation();
  const manualAccounts = accounts.filter((a) => !a.connection_id);
  const [accountId, setAccountId] = useState(() =>
    manualAccounts.length === 1 ? manualAccounts[0].id : '',
  );
  const [date, setDate] = useState(() => todayIso());
  const [direction, setDirection] = useState<'inflow' | 'outflow'>('outflow');
  const [amount, setAmount] = useState('');
  const [counterparty, setCounterparty] = useState('');
  const [docNo, setDocNo] = useState('');
  const [purpose, setPurpose] = useState('');
  const [category, setCategory] = useState('');
  const [amountError, setAmountError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const id = useId();

  const account = manualAccounts.find((a) => a.id === accountId);
  const value = parseMoney(amount);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (isLoading) return;
    if (value === null || value <= 0) {
      setAmountError('Укажите сумму больше нуля, например 125 000,50');
      return;
    }
    if (!account) {
      setError('Выберите счёт, по которому прошла операция');
      return;
    }
    setAmountError(null);
    setError(null);
    try {
      await createPayment({
        account_id: account.id,
        payment_date: date,
        direction,
        amount: value,
        counterparty_name: counterparty.trim(),
        purpose: purpose.trim() || 'Операционный платёж',
        category: category || undefined,
        document_number: docNo.trim() || undefined,
      }).unwrap();
      onClose();
    } catch (err: unknown) {
      setError(apiErrorText(err, 'Не удалось записать операцию'));
    }
  }

  return (
    <FormBox as="form" onSubmit={handleSubmit}>
      <FormTitle>Операция вручную</FormTitle>
      <FormGrid>
        <FieldBox>
          <FieldLabel htmlFor={`${id}-account`}>Счёт</FieldLabel>
          <Select
            id={`${id}-account`}
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            required
          >
            <option value="">Выберите счёт…</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id} disabled={Boolean(a.connection_id)}>
                {a.bank_name} ({accountTail(a.account_number)})
                {a.connection_id ? ' — операции из банка' : ''}
              </option>
            ))}
          </Select>
        </FieldBox>
        <FieldBox>
          <FieldLabel htmlFor={`${id}-date`}>Дата операции</FieldLabel>
          <Input
            id={`${id}-date`}
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
          />
        </FieldBox>
        <FieldBox>
          <FieldLabel htmlFor={`${id}-direction`}>Приход или расход</FieldLabel>
          <Select
            id={`${id}-direction`}
            value={direction}
            onChange={(e) => setDirection(e.target.value as 'inflow' | 'outflow')}
          >
            <option value="outflow">Списание (расход)</option>
            <option value="inflow">Поступление (приход)</option>
          </Select>
        </FieldBox>
        <FieldBox>
          <FieldLabel htmlFor={`${id}-category`}>Категория ДДС</FieldLabel>
          <Select
            id={`${id}-category`}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">Определить по назначению</option>
            {CATEGORY_OPTIONS.map(([code, label]) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </Select>
        </FieldBox>
        <FieldBox>
          <FieldLabel htmlFor={`${id}-amount`}>Сумма, ₽</FieldLabel>
          <Input
            id={`${id}-amount`}
            type="text"
            inputMode="decimal"
            placeholder="125 000,50"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setAmountError(null);
            }}
            aria-invalid={amountError ? true : undefined}
            aria-describedby={amountError ? `${id}-amount-error` : undefined}
            required
          />
          {amountError && <FieldError id={`${id}-amount-error`}>{amountError}</FieldError>}
        </FieldBox>
        <FieldBox>
          <FieldLabel htmlFor={`${id}-counterparty`}>Контрагент</FieldLabel>
          <Input
            id={`${id}-counterparty`}
            value={counterparty}
            onChange={(e) => setCounterparty(e.target.value)}
            required
          />
        </FieldBox>
        <FieldBox>
          <FieldLabel htmlFor={`${id}-doc`}>№ платёжного поручения</FieldLabel>
          <Input
            id={`${id}-doc`}
            placeholder="напр. ПП-1890"
            value={docNo}
            onChange={(e) => setDocNo(e.target.value)}
          />
        </FieldBox>
        <FieldBox>
          <FieldLabel htmlFor={`${id}-purpose`}>Назначение платежа</FieldLabel>
          <Input
            id={`${id}-purpose`}
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            required
          />
        </FieldBox>
      </FormGrid>
      <ManualNotice>
        Запись меняет учётный остаток счёта в ОСНОВЕ. В банк ничего не отправляется.
        {account && value !== null && value > 0 && (
          <>
            {' '}
            Остаток счёта {account.bank_name} {accountTail(account.account_number)}{' '}
            {direction === 'inflow' ? 'увеличится' : 'уменьшится'} на {formatMoney(value)}.
          </>
        )}
      </ManualNotice>
      {error && <ErrorNotice role="alert">{error}</ErrorNotice>}
      <FormActions>
        <WrapButton $size="sm" type="submit" disabled={isLoading}>
          {isLoading ? 'Сохранение…' : 'Записать операцию'}
        </WrapButton>
        <Button $size="sm" $variant="ghost" type="button" onClick={onClose}>
          Отмена
        </Button>
      </FormActions>
    </FormBox>
  );
}
